// src/screens/crew/CrewNoticesScreen.js
// 78차 70차: 신규 — 공지사항이 채팅형 피드에 섞여 있어 새로 가입한 멤버가 지난 공지를
// 놓치기 쉽다는 요청으로, 공지만 모아보는 전용 화면 추가. 크루장/관리자는 이 화면에서
// 바로 수정/삭제 가능(CrewManageScreen의 "공지사항 작성"은 신규 작성 전용으로 유지).
import React, { useState, useCallback, useRef } from 'react';
import { showAlert } from '../../components/common/AppAlert';
import {
  View, Text, TextInput, StyleSheet,
  ScrollView, ActivityIndicator,
} from 'react-native';
import { useFocusEffect } from '@react-navigation/native';
import AppHeader from '../../components/common/AppHeader';
import GradientButton from '../../components/common/GradientButton';
import { SecondaryButton, DangerButton } from '../../components/common/Buttons';
import { COLORS } from '../../constants/theme';
import { getCrewNotices, updateCrewNotice, deleteCrewPost } from '../../services/crewService';
import { MenuBellIcon } from '../../components/common/Icons';

const GOLD = COLORS.gold, BG = COLORS.bg, SURFACE = COLORS.surface, SURFACE2 = COLORS.surface2;
const BORDER = COLORS.border, TEXT = COLORS.text, TEXT_MUTED = COLORS.textMuted;

const formatDate = (createdAt) => {
  const d = createdAt?.toDate ? createdAt.toDate() : (createdAt ? new Date(createdAt) : null);
  if (!d) return '';
  return `${d.getFullYear()}.${String(d.getMonth() + 1).padStart(2, '0')}.${String(d.getDate()).padStart(2, '0')}`;
};

function NoticeCard({ item, canManage, onSaved, onDeleted }) {
  const [editing, setEditing] = useState(false);
  const [title, setTitle] = useState(item.title || '');
  const [text, setText] = useState(item.text || '');
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!text.trim()) { showAlert('알림', '공지 내용을 입력해주세요.'); return; }
    setSaving(true);
    try {
      await updateCrewNotice(item.id, { title, text });
      onSaved(item.id, { title: title.trim(), text: text.trim() });
      setEditing(false);
    } catch {
      showAlert('오류', '공지 수정에 실패했어요.');
    } finally { setSaving(false); }
  };

  const handleDelete = () => {
    showAlert('공지 삭제', '이 공지를 삭제하시겠어요?', [
      { text: '취소', style: 'cancel' },
      { text: '삭제', style: 'destructive', onPress: async () => {
        try { await deleteCrewPost(item.id); onDeleted(item.id); }
        catch { showAlert('오류', '삭제에 실패했어요.'); }
      } },
    ]);
  };

  if (editing) {
    return (
      <View style={s.card}>
        <TextInput style={s.input} value={title} onChangeText={setTitle} placeholder="제목 (선택)" placeholderTextColor={TEXT_MUTED} maxLength={30} />
        <TextInput style={[s.input, s.textArea]} value={text} onChangeText={setText} placeholder="공지 내용" placeholderTextColor={TEXT_MUTED} multiline maxLength={300} />
        <View style={{ flexDirection: 'row', gap: 8, marginTop: 10 }}>
          <SecondaryButton label="취소" onPress={() => { setEditing(false); setTitle(item.title || ''); setText(item.text || ''); }} style={{ flex: 1 }} disabled={saving} />
          <GradientButton label={saving ? '저장 중...' : '저장'} onPress={handleSave} style={{ flex: 1 }} disabled={saving} />
        </View>
      </View>
    );
  }

  return (
    <View style={s.card}>
      <View style={{ flexDirection: 'row', alignItems: 'flex-start', justifyContent: 'space-between' }}>
        <View style={{ flex: 1 }}>
          {!!item.title && <Text style={s.title}>{item.title}</Text>}
          <Text style={s.date}>{item.nickname} · {formatDate(item.createdAt)}</Text>
        </View>
      </View>
      <Text style={s.text}>{item.text}</Text>
      {canManage && (
        <View style={{ flexDirection: 'row', gap: 8, marginTop: 12 }}>
          <SecondaryButton label="수정" onPress={() => setEditing(true)} style={s.smallBtn} textStyle={s.smallBtnText} />
          <DangerButton label="삭제" onPress={handleDelete} style={s.smallBtn} textStyle={s.smallBtnText} />
        </View>
      )}
    </View>
  );
}

export default function CrewNoticesScreen({ navigation, route }) {
  const { crewId, canManage } = route.params || {};
  const [notices, setNotices] = useState([]);
  const [loading, setLoading] = useState(true);
  // 79차: 화면을 빠르게 나갔다 다시 들어오면 이전 포커스의 load()가 늦게 응답해 최신
  // 목록을 오래된 것으로 덮어쓸 수 있었음 — activeRef로 이전 포커스 세션의 setState만 무시.
  const activeRef = useRef(true);

  const load = useCallback(async () => {
    if (!crewId) { if (activeRef.current) setLoading(false); return; }
    try {
      const list = await getCrewNotices(crewId);
      if (activeRef.current) setNotices(list);
    }
    catch { if (activeRef.current) setNotices([]); }
    finally { if (activeRef.current) setLoading(false); }
  }, [crewId]);

  useFocusEffect(useCallback(() => {
    activeRef.current = true;
    load();
    return () => { activeRef.current = false; };
  }, [load]));

  const handleSaved = (id, patch) => setNotices(prev => prev.map(n => n.id === id ? { ...n, ...patch } : n));
  const handleDeleted = (id) => setNotices(prev => prev.filter(n => n.id !== id));

  return (
    <View style={s.container}>
      <AppHeader showBack centerElement={<Text style={s.headerTitle}>공지사항</Text>} />
      {loading ? (
        <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center' }}>
          <ActivityIndicator color={GOLD} />
        </View>
      ) : (
        <ScrollView contentContainerStyle={{ padding: 16, paddingBottom: 40 }}>
          {notices.length === 0 ? (
            <View style={{ alignItems: 'center', marginTop: 60, gap: 10 }}>
              <MenuBellIcon size={28} color={TEXT_MUTED} />
              <Text style={{ color: TEXT_MUTED, fontSize: 13 }}>아직 등록된 공지가 없어요.</Text>
            </View>
          ) : (
            notices.map(item => (
              <NoticeCard key={item.id} item={item} canManage={canManage} onSaved={handleSaved} onDeleted={handleDeleted} />
            ))
          )}
        </ScrollView>
      )}
    </View>
  );
}

const s = StyleSheet.create({
  container:   { flex: 1, backgroundColor: BG },
  headerTitle: { color: TEXT, fontSize: 15, fontWeight: '800' },
  card:        { backgroundColor: SURFACE, borderRadius: 14, borderWidth: 1, borderColor: COLORS.goldBorder, padding: 14, marginBottom: 10 },
  title:       { color: TEXT, fontSize: 14, fontWeight: '800', marginBottom: 3 },
  date:        { color: TEXT_MUTED, fontSize: 11 },
  text:        { color: TEXT, fontSize: 13, lineHeight: 19, marginTop: 8 },
  input:       { backgroundColor: SURFACE2, borderWidth: 1, borderColor: BORDER, borderRadius: 8,
                 paddingHorizontal: 12, paddingVertical: 10, color: TEXT, fontSize: 13, marginBottom: 8 },
  textArea:    { minHeight: 70, textAlignVertical: 'top' },
  smallBtn:    { paddingHorizontal: 12, paddingVertical: 6 },
  smallBtnText:{ fontSize: 11 },
});
